import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  CreditCard,
  RefreshCw,
  ArrowUp,
  Activity,
  ShoppingCart,
  Globe,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { DashboardStats } from '@/types'
import { dashboardService } from '@/services/dashboardService'

export default function Analytics() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [searchParams, _setSearchParams] = useSearchParams()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, _setTimeRange] = useState(searchParams.get('range') || '30d')

  const fetchStats = async () => {
    setLoading(true)
    try {
      const response = await dashboardService.getDashboardStats()
      setStats(response)
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [timeRange])

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(amount))
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mr-3 text-gray-400" />
            <p className="text-gray-500">Loading analytics...</p>
          </div>
        ) : stats ? (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="card">
                <div className="card-content">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(stats.total_revenue)}
                      </p>
                      <div className="flex items-center mt-1">
                        <ArrowUp className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm text-green-600">+12.5%</span>
                      </div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-full">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-content">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(stats.total_transactions)}
                      </p>
                      <div className="flex items-center mt-1">
                        <ArrowUp className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm text-green-600">+8.2%</span>
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-full">
                      <CreditCard className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-content">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Active Customers</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(stats.total_customers)}
                      </p>
                      <div className="flex items-center mt-1">
                        <ArrowUp className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm text-green-600">+15.3%</span>
                      </div>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-full">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-content">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Success Rate</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {((stats.completed_transactions / stats.total_transactions) * 100).toFixed(
                          1
                        )}
                        %
                      </p>
                      <div className="flex items-center mt-1">
                        <ArrowUp className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm text-green-600">+2.1%</span>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-full">
                      <TrendingUp className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Status Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title text-xl">Transaction Status</h3>
                  <p className="card-description">Distribution of transaction statuses</p>
                </div>
                <div className="card-content">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-50 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <span className="font-medium text-gray-900">Completed</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {formatNumber(stats.completed_transactions)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {(
                            (stats.completed_transactions / stats.total_transactions) *
                            100
                          ).toFixed(1)}
                          %
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-yellow-50 rounded-lg">
                          <Clock className="h-5 w-5 text-yellow-600" />
                        </div>
                        <span className="font-medium text-gray-900">Pending</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {formatNumber(stats.pending_transactions)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {((stats.pending_transactions / stats.total_transactions) * 100).toFixed(
                            1
                          )}
                          %
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-50 rounded-lg">
                          <XCircle className="h-5 w-5 text-red-600" />
                        </div>
                        <span className="font-medium text-gray-900">Failed</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {formatNumber(stats.failed_transactions)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {((stats.failed_transactions / stats.total_transactions) * 100).toFixed(
                            1
                          )}
                          %
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title text-xl">Sales Channels</h3>
                  <p className="card-description">Active sales channels performance</p>
                </div>
                <div className="card-content">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <ShoppingCart className="h-5 w-5 text-blue-600" />
                        </div>
                        <span className="font-medium text-gray-900">Active Channels</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {formatNumber(stats.active_sale_shops)}
                        </p>
                        <p className="text-sm text-gray-500">Channels</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-50 rounded-lg">
                          <Globe className="h-5 w-5 text-purple-600" />
                        </div>
                        <span className="font-medium text-gray-900">Web Integrations</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {Math.floor(stats.active_sale_shops * 0.7)}
                        </p>
                        <p className="text-sm text-gray-500">Websites</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-50 rounded-lg">
                          <Activity className="h-5 w-5 text-green-600" />
                        </div>
                        <span className="font-medium text-gray-900">API Integrations</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {Math.floor(stats.active_sale_shops * 0.3)}
                        </p>
                        <p className="text-sm text-gray-500">APIs</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title text-xl">Recent Transactions</h3>
                  <p className="card-description">Latest transaction activity</p>
                </div>
                <div className="card-content">
                  <div className="space-y-4">
                    {stats.recent_transactions.slice(0, 5).map((transaction, index) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0"
                        style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: statusColors.success.backgroundColor }}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-sm" style={pageStyles.text}>
                              {transaction.transaction_reference}
                            </p>
                            <p className="text-xs" style={pageStyles.textSecondary}>
                              {new Date(transaction.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm" style={pageStyles.text}>
                            {formatCurrency(transaction.total_amount)}
                          </p>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${transaction.status === 'completed'
                              ? 'text-green-600 bg-green-50'
                              : transaction.status === 'pending'
                                ? 'text-yellow-600 bg-yellow-50'
                                : 'text-red-600 bg-red-50'
                              }`}
                          >
                            {transaction.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title text-xl">Recent Notifications</h3>
                  <p className="card-description">Latest system notifications</p>
                </div>
                <div className="card-content">
                  <div className="space-y-4">
                    {stats.recent_notifications.slice(0, 5).map((notification, index) => (
                      <div
                        key={notification.id}
                        className="flex items-start space-x-3 py-3 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm">{notification.title}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notification.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="card">
            <div className="card-content text-center py-12">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Failed to load analytics data</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
